import React, { useState, useEffect } from 'react';

interface ImageModalProps {
  imageUrl: string;
  alt: string;
  images?: string[];
  initialIndex?: number;
  onClose: () => void;
}

export const ImageModal: React.FC<ImageModalProps> = ({ 
  imageUrl, 
  alt, 
  images,
  initialIndex = 0,
  onClose 
}) => {
  const allImages = images && images.length > 0 ? images : [imageUrl];
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (images && images.length > 0) {
      const index = images.findIndex(img => img === imageUrl);
      setCurrentIndex(index >= 0 ? index : 0);
    } else {
      setCurrentIndex(0);
    }
  }, [imageUrl, images]);

  const currentImage = allImages[currentIndex] || imageUrl;

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
    } else if (e.key === 'ArrowRight') {
      setCurrentIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [allImages.length]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none fade-in"
      onClick={onClose}
    >
      <div 
        className="relative max-w-7xl max-h-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-4 border border-gray-300 dark:border-gray-700 pointer-events-auto zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 z-50 bg-white dark:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 bg-white dark:bg-gray-700 bg-opacity-90 dark:bg-opacity-90 text-gray-900 dark:text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 dark:hover:bg-opacity-100 transition-all shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 bg-white dark:bg-gray-700 bg-opacity-90 dark:bg-opacity-90 text-gray-900 dark:text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 dark:hover:bg-opacity-100 transition-all shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded z-40">
              {currentIndex + 1} / {allImages.length}
            </div>
          </>
        )}
        
        <img
          src={currentImage}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain rounded"
        />
      </div>
    </div>
  );
};

